import React, { FC, useCallback, useMemo, useState } from 'react';
import { SettingsCloseButton, SettingsMenuList, SettingsModalStyled } from './SettingsModal.style';
import { IonIcon, isPlatform, useIonAlert } from '@ionic/react';
import {
  barChartOutline,
  cardOutline,
  closeCircleOutline,
  earthOutline,
  exitOutline,
  giftOutline,
  personOutline,
} from 'ionicons/icons';
import MyInfoComponent from './MyInfoComponent/MyInfoComponent';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../../redux/user/selector';
import { useIntl } from 'react-intl';
import AddAvatarModal from '../AddAvatarModal';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { subscriptionsSelector } from '../../redux/subscriptions/selector';
import useLangs from '../../context/LangsContext';
import { useHistory } from 'react-router';
import BuildInformation from '../../components/BuildInformation/BuildInformation';
import { logout } from '../../redux/user/reducer';
import useToast from '../../context/ToastContext/ToastContext';
import { fetchSpecialistsList } from '../../redux/specialists/thunk';
import { resetSpecialist } from '../../redux/specialists/reducer';
import InfoCircle from './icons/info_circle.svg?react';
import { useTranslation } from 'react-i18next';
import { getUserOnBoardingFlowKeySelector } from 'src/redux/onBoardingFlow/selector';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';
import { useAuthFlow } from 'src/shared/hooks/useAuthFlow';
import { ChangeLanguageModalWithLanguageWorkInProgressFeature } from 'src/features/ChangeLanguageModalWithLanguageWorkInProgressFeature';

type SettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const SettingsModal: FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const intl = useIntl();
  const { triggerSignOutFlow } = useAuthFlow();
  const { t } = useTranslation('profile');
  const [present] = useIonAlert();
  const { showMessenger } = useIntercomPlugin();
  const { showToast } = useToast();
  const history = useHistory();
  const dispatch = useDispatch();
  const { locale } = useLangs();

  const user = useSelector(userSelector);
  const subscriptions = useSelector(subscriptionsSelector());
  const onboardingKey = useSelector(getUserOnBoardingFlowKeySelector);

  const [showChangeImageModal, setShowChangeImageModal] = useState(false);
  const [changeLanguageShow, setChangeLanguageShow] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'settings.subscriptions',
          defaultMessage: 'Підписка на терапію',
        }),
        icon: barChartOutline,
        href: PrivateRoutes.SUBSCRIPTION_CONTROL,
        disabled: !subscriptions.data?.length,
      },
      {
        title: intl.formatMessage({
          id: 'settings.my',
          defaultMessage: 'Мої дані',
        }),
        icon: personOutline,
        href: PrivateRoutes.PROFILE_INFO,
      },
      {
        title: intl.formatMessage({
          id: 'settings.card',
          defaultMessage: 'Мої картки',
        }),
        icon: cardOutline,
        href: PrivateRoutes.PROFILE_CARDS,
      },
      {
        title: t('profile.discounts.item'),
        icon: giftOutline,
        href: PrivateRoutes.DISCOUNTS,
      },
      {
        title: intl.formatMessage({
          id: 'settings.lang',
          defaultMessage: 'Мова',
        }),
        icon: earthOutline,
        onClick: () => setChangeLanguageShow(true),
      },
      {
        title: intl.formatMessage({
          id: 'settings.intercom_info',
          defaultMessage: 'Служба підтримки',
        }),
        customIcon: <InfoCircle />,
        onClick: () => showMessenger(),
      },
      {
        title: intl.formatMessage({
          id: 'settings.logout',
          defaultMessage: 'Вихід',
        }),
        icon: exitOutline,
        onClick: () => {
          present({
            mode: 'ios',
            header: intl.formatMessage({
              id: 'general.logout.question',
              defaultMessage: 'Підтвердити вихід',
            }),
            buttons: [
              {
                text: intl.formatMessage({
                  id: 'general.logout.btn',
                  defaultMessage: 'Вийти',
                }),
                cssClass: 'confirm-delete-button',
                handler: async () => {
                  await exitButtonHandler();
                },
              },
              {
                text: intl.formatMessage({
                  id: 'general.no',
                  defaultMessage: 'Ні',
                }),
              },
            ],
          });
        },
      },
    ],
    [subscriptions?.data, subscriptions?.data?.length, locale, intl.formatMessage, t, onboardingKey]
  );

  const exitButtonHandler = useCallback(async () => {
    try {
      await triggerSignOutFlow();
      dispatch(logout());
      history.replace(PublicRoutes.AUTH);
    } catch (error) {
      showToast({ error });
    }
  }, []);

  const clickChangeImageHandler = useCallback(() => {
    setShowChangeImageModal(true);
  }, []);

  return (
    <>
      <SettingsModalStyled
        isOpen={isOpen}
        initialBreakpoint={1}
        breakpoints={[0, 1]}
        mode="ios"
        swipeToClose
        onIonModalDidDismiss={onClose}
      >
        <SettingsCloseButton onClick={onClose}>
          <IonIcon icon={closeCircleOutline} />
        </SettingsCloseButton>
        <div className={'my-info'}>
          <MyInfoComponent
            showSkeleton={false}
            myPhotoSrc={user?.avatar}
            buttonText={intl.formatMessage({
              id: 'image.change.link',
              defaultMessage: 'Змінити фотографію',
            })}
            buttonHandler={clickChangeImageHandler}
          />
        </div>
        <SettingsMenuList>
          {menuItems
            .filter((item) => !!item)
            .map((item, index) =>
              !item ? null : (
                <li
                  key={index}
                  className={item.disabled ? 'disabled' : ''}
                  onClick={async () => {
                    if (item.disabled) return;

                    if (item?.href) {
                      onClose();
                      history.push(item.href);
                    }

                    item?.onClick && (await item.onClick());
                  }}
                >
                  {item.title}
                  {item.customIcon ? item.customIcon : <IonIcon icon={item.icon} />}
                </li>
              )
            )}
        </SettingsMenuList>
        <BuildInformation className={`mt-11 ${isPlatform('hybrid') ? '' : 'mb-5'}`} />
      </SettingsModalStyled>
      <AddAvatarModal
        isOpen={showChangeImageModal}
        onDidDismiss={() => setShowChangeImageModal(false)}
        defaultImage={user?.avatar}
      />
      <ChangeLanguageModalWithLanguageWorkInProgressFeature
        isOpen={changeLanguageShow}
        closeModal={() => setChangeLanguageShow(false)}
        onSelect={(l) => {
          dispatch(resetSpecialist());
          dispatch(fetchSpecialistsList(l));
        }}
      />
    </>
  );
};

export default React.memo(SettingsModal);
